import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { HiArrowLeft, HiClipboard, HiTrash, HiRefresh, HiClock, HiPlus, HiX, HiPencil, HiDesktopComputer, HiCheck, HiPause, HiSwitchHorizontal, HiDownload, HiKey } from 'react-icons/hi';
import { Button, Card, Badge, Table, Input } from '../components/ui';
import { getLicense, deactivateInstallation, reactivateInstallation, deleteInstallation, revokeLicense, getLicenseHistory, updateLicense, downloadOfflineLicense } from '../services/licenses.api';
import { useAuthStore } from '../store/authStore';
import { formatDate, formatDateTime, formatRelative, copyToClipboard, getRuntimeLabel, getHistoryActionLabel, parseJSON } from '../utils/helpers';
import { getErrorMessage } from '../services/api';
import type { Installation, LicenseHistory } from '../types';

export function LicenseDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'OWNER';
  const canManage = user?.role === 'ADMIN' || user?.role === 'OWNER' || user?.role === 'INVOICE_TEAM';

  const [editingHardwareId, setEditingHardwareId] = useState(false);
  const [hardwareIdInput, setHardwareIdInput] = useState('');
  const [downloading, setDownloading] = useState(false);

  const { data: license, isLoading } = useQuery({
    queryKey: ['license', id],
    queryFn: () => getLicense(id!),
    enabled: !!id,
  });

  const { data: history } = useQuery({
    queryKey: ['licenseHistory', id],
    queryFn: () => getLicenseHistory(id!),
    enabled: !!id,
  });

  const revokeMutation = useMutation({
    mutationFn: () => revokeLicense(id!),
    onSuccess: () => {
      toast.success('License revoked');
      queryClient.invalidateQueries({ queryKey: ['license', id] });
      queryClient.invalidateQueries({ queryKey: ['licenseHistory', id] });
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const reactivateLicenseMutation = useMutation({
    mutationFn: () => updateLicense(id!, { status: 'ACTIVE' }),
    onSuccess: () => {
      toast.success('Lizenz reaktiviert');
      queryClient.invalidateQueries({ queryKey: ['license', id] });
      queryClient.invalidateQueries({ queryKey: ['licenseHistory', id] });
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const saveHardwareIdMutation = useMutation({
    mutationFn: (hardwareId: string) => updateLicense(id!, { hardwareId }),
    onSuccess: () => {
      toast.success('Hardware-ID gespeichert, Offline-Lizenz aktualisiert');
      queryClient.invalidateQueries({ queryKey: ['license', id] });
      queryClient.invalidateQueries({ queryKey: ['licenseHistory', id] });
      setEditingHardwareId(false);
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const handleDownloadOffline = async () => {
    if (!license) return;
    try {
      setDownloading(true);
      await downloadOfflineLicense(license.id, license.licenseKey);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setDownloading(false);
    }
  };

  const deactivateMutation = useMutation({
    mutationFn: (installationId: string) => deactivateInstallation(id!, installationId),
    onSuccess: () => {
      toast.success('Installation deactivated');
      queryClient.invalidateQueries({ queryKey: ['license', id] });
      queryClient.invalidateQueries({ queryKey: ['licenseHistory', id] });
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const reactivateMutation = useMutation({
    mutationFn: (installationId: string) => reactivateInstallation(id!, installationId),
    onSuccess: () => {
      toast.success('Installation reactivated');
      queryClient.invalidateQueries({ queryKey: ['license', id] });
      queryClient.invalidateQueries({ queryKey: ['licenseHistory', id] });
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const deleteMutation = useMutation({
    mutationFn: (installationId: string) => deleteInstallation(id!, installationId),
    onSuccess: () => {
      toast.success('Installation deleted');
      queryClient.invalidateQueries({ queryKey: ['license', id] });
      queryClient.invalidateQueries({ queryKey: ['licenseHistory', id] });
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!license) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">License not found</p>
      </div>
    );
  }

  const handleCopyKey = () => {
    copyToClipboard(license.licenseKey);
    toast.success('License key copied to clipboard');
  };

  const getHistoryIcon = (action: string) => {
    const icons: Record<string, React.ReactNode> = {
      CREATED: <HiPlus className="w-4 h-4 text-green-500" />,
      EXTENDED: <HiClock className="w-4 h-4 text-blue-500" />,
      REVOKED: <HiX className="w-4 h-4 text-red-500" />,
      SUSPENDED: <HiPause className="w-4 h-4 text-yellow-500" />,
      REACTIVATED: <HiCheck className="w-4 h-4 text-green-500" />,
      DEVICE_COUNT_CHANGED: <HiDesktopComputer className="w-4 h-4 text-purple-500" />,
      STATUS_CHANGED: <HiSwitchHorizontal className="w-4 h-4 text-blue-500" />,
      RENEWED: <HiRefresh className="w-4 h-4 text-green-500" />,
      NOTES_UPDATED: <HiPencil className="w-4 h-4 text-gray-500" />,
      INSTALLATION_DEACTIVATED: <HiPause className="w-4 h-4 text-yellow-500" />,
      INSTALLATION_REACTIVATED: <HiRefresh className="w-4 h-4 text-green-500" />,
      INSTALLATION_DELETED: <HiTrash className="w-4 h-4 text-red-500" />,
    };
    return icons[action] || <HiClock className="w-4 h-4 text-gray-500" />;
  };

  const formatHistoryDetails = (entry: LicenseHistory) => {
    const prev = parseJSON<Record<string, unknown>>(entry.previousValue, {});
    const next = parseJSON<Record<string, unknown>>(entry.newValue, {});

    if (entry.action === 'DEVICE_COUNT_CHANGED') {
      return `${prev.deviceCount} → ${next.deviceCount} devices`;
    }
    if (entry.action === 'STATUS_CHANGED') {
      return `${prev.status} → ${next.status}`;
    }
    if (entry.action === 'EXTENDED') {
      return `Extended by ${next.runtime || 'N/A'}`;
    }
    if (entry.action === 'CREATED') {
      return `${next.productName || ''} - ${next.deviceCount || 1} device(s)`;
    }
    return null;
  };

  const installationColumns = [
    { key: 'installationId', header: 'Installation ID' },
    { key: 'deviceName', header: 'Device Name', render: (i: Installation) => i.deviceName || '-' },
    {
      key: 'isActive',
      header: 'Status',
      render: (i: Installation) => (
        <Badge status={i.isActive ? 'ACTIVE' : 'EXPIRED'}>
          {i.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    { key: 'lastCheckIn', header: 'Last Check-in', render: (i: Installation) => formatRelative(i.lastCheckIn) },
    {
      key: 'actions',
      header: 'Actions',
      render: (i: Installation) => (
        <div className="flex gap-1">
          {i.isActive ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => deactivateMutation.mutate(i.id)}
              title="Deactivate"
            >
              <HiPause className="w-4 h-4 text-yellow-500" />
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => reactivateMutation.mutate(i.id)}
              title="Reactivate"
            >
              <HiRefresh className="w-4 h-4 text-green-500" />
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (confirm('Are you sure you want to permanently delete this installation?')) {
                  deleteMutation.mutate(i.id);
                }
              }}
              title="Delete"
            >
              <HiTrash className="w-4 h-4 text-red-500" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate('/licenses')}>
          <HiArrowLeft className="w-4 h-4 mr-2" />
          Back to Licenses
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">License Details</h2>
                <p className="text-sm text-gray-500 mt-1">License information and status</p>
              </div>
              <Badge status={license.status}>{license.status}</Badge>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-500">License Key</p>
                <div className="flex items-center gap-2 mt-1">
                  <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                    {license.licenseKey}
                  </code>
                  <Button variant="ghost" size="sm" onClick={handleCopyKey}>
                    <HiClipboard className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-500">Product</p>
                <p className="font-medium mt-1">{license.product.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Runtime</p>
                <p className="font-medium mt-1">{getRuntimeLabel(license.runtime)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Device Count</p>
                <p className="font-medium mt-1">
                  {license.installations?.filter((i) => i.isActive).length || 0} / {license.deviceCount}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Issued</p>
                <p className="font-medium mt-1">{formatDate(license.issuedAt)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Expires</p>
                <p className="font-medium mt-1">{formatDate(license.expiresAt)}</p>
              </div>
            </div>

            {license.notes && (
              <div className="mt-6 pt-6 border-t">
                <p className="text-sm text-gray-500">Notes</p>
                <p className="mt-1">{license.notes}</p>
              </div>
            )}

            {/* Offline license (signed token) */}
            <div className="mt-6 pt-6 border-t">
              <div className="flex items-center gap-2 mb-3">
                <HiKey className="w-5 h-5 text-gray-400" />
                <h3 className="text-sm font-semibold text-gray-900">Offline-Lizenz</h3>
              </div>

              <div className="flex flex-col gap-1">
                <p className="text-sm text-gray-500">Hardware-ID</p>
                {editingHardwareId ? (
                  <div className="flex items-start gap-2 mt-1">
                    <div className="flex-1">
                      <Input
                        type="text"
                        value={hardwareIdInput}
                        onChange={(e) => setHardwareIdInput(e.target.value)}
                        placeholder="z. B. 35840662ca045fe8"
                      />
                    </div>
                    <Button
                      size="sm"
                      isLoading={saveHardwareIdMutation.isPending}
                      onClick={() => saveHardwareIdMutation.mutate(hardwareIdInput.trim())}
                    >
                      Speichern
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => setEditingHardwareId(false)}>
                      Abbrechen
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 mt-1">
                    <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                      {license.hardwareId || 'nicht gesetzt'}
                    </code>
                    {canManage && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setHardwareIdInput(license.hardwareId || '');
                          setEditingHardwareId(true);
                        }}
                        title="Hardware-ID bearbeiten"
                      >
                        <HiPencil className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                )}
              </div>

              <div className="mt-4">
                {license.offlineLicenseToken ? (
                  <>
                    <Button onClick={handleDownloadOffline} isLoading={downloading}>
                      <HiDownload className="w-4 h-4 mr-2" />
                      Offline-Lizenz herunterladen
                    </Button>
                    {license.offlineIssuedAt && (
                      <p className="text-xs text-gray-400 mt-2">
                        Token erstellt: {formatDateTime(license.offlineIssuedAt)}
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <Button disabled>
                      <HiDownload className="w-4 h-4 mr-2" />
                      Offline-Lizenz herunterladen
                    </Button>
                    <p className="text-xs text-gray-500 mt-2">
                      Bitte zuerst die Hardware-ID hinterlegen.
                    </p>
                  </>
                )}
              </div>
            </div>

            {isAdmin && license.status === 'ACTIVE' && (
              <div className="mt-6 pt-6 border-t flex gap-3">
                <Button variant="danger" onClick={() => revokeMutation.mutate()}>
                  <HiRefresh className="w-4 h-4 mr-2" />
                  Revoke License
                </Button>
              </div>
            )}

            {isAdmin && (license.status === 'REVOKED' || license.status === 'SUSPENDED') && (
              <div className="mt-6 pt-6 border-t flex gap-3">
                <Button onClick={() => reactivateLicenseMutation.mutate()} isLoading={reactivateLicenseMutation.isPending}>
                  <HiCheck className="w-4 h-4 mr-2" />
                  Reaktivieren
                </Button>
              </div>
            )}
          </Card>

          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Installations</h3>
            <Table
              columns={installationColumns}
              data={license.installations || []}
              keyExtractor={(i) => i.id}
              emptyMessage="No installations"
            />
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer</h3>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="font-medium">{license.customer.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium">{license.customer.email}</p>
              </div>
              {license.customer.company && (
                <div>
                  <p className="text-sm text-gray-500">Company</p>
                  <p className="font-medium">{license.customer.company}</p>
                </div>
              )}
            </div>
          </Card>

          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">History</h3>
            {history && history.length > 0 ? (
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {history.map((entry) => (
                  <div key={entry.id} className="flex gap-3 pb-4 border-b border-gray-100 last:border-0 last:pb-0">
                    <div className="flex-shrink-0 mt-0.5">
                      <div className="p-1.5 bg-gray-100 rounded-full">
                        {getHistoryIcon(entry.action)}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {getHistoryActionLabel(entry.action)}
                      </p>
                      {formatHistoryDetails(entry) && (
                        <p className="text-sm text-gray-600 mt-0.5">
                          {formatHistoryDetails(entry)}
                        </p>
                      )}
                      {entry.notes && (
                        <p className="text-sm text-gray-500 mt-1 italic">
                          "{entry.notes}"
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-xs text-gray-400">
                          {formatDateTime(entry.createdAt)}
                        </p>
                        {entry.performedByName && (
                          <p className="text-xs text-gray-400">
                            by {entry.performedByName}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No history available</p>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
